import { accountReceivableApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetAccountReceivableParamsI,
  ListAccountReceivableI,
  OneAccountReceivableForFrontI,
  RegisterPaymentI,
  NewAccountReceivableI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getAccountReceivables = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  customerId,
  statusCode,
  dateFrom,
  dateTo,
}: GetAccountReceivableParamsI): Promise<ServiceResponseI<DataI<ListAccountReceivableI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId)   params.append("branchId",   branchId);
    if (customerId) params.append("customerId", customerId);
    if (statusCode) params.append("statusCode", statusCode);
    if (dateFrom)   params.append("dateFrom",   dateFrom);
    if (dateTo)     params.append("dateTo",     dateTo);

    const { data } = await accountReceivableApi.get<ServiceResponseI<DataI<ListAccountReceivableI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountReceivableById = async (
  id: string,
): Promise<ServiceResponseI<OneAccountReceivableForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountReceivableApi.get<ServiceResponseI<OneAccountReceivableForFrontI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountReceivableRegisterPayment = async (
  payload: RegisterPaymentI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountReceivableApi.post<ServiceResponseI<string>>(
      "/payment",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountReceivablePost = async (
  payload: NewAccountReceivableI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountReceivableApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const accountReceivableAnnul = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await accountReceivableApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useAccountReceivables = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  customerId,
  statusCode,
  dateFrom,
  dateTo,
}: GetAccountReceivableParamsI) => {
  const accountReceivablesQuery = useQuery(
    ["account-receivables", { pagination, sorting, globalFilter, branchId, customerId, statusCode, dateFrom, dateTo }],
    () => getAccountReceivables({ pagination, sorting, globalFilter, branchId, customerId, statusCode, dateFrom, dateTo }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData:     true,
    },
  );

  return { accountReceivablesQuery };
};